import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getClubEvents, getEventPublishedBundles } from '../lib/firebase';
import * as marksStore from '../lib/marksStore';
import MarksMap from '../components/MarksMap';
import PublishStateNote from '../components/PublishStateNote';
import { loadAllClubs } from '../lib/clubStore';
import { fetchIrishLightsMarks } from '../lib/irishLightsFeed';
import { summarizeLibraryImport, pickImportableMarks, importableIds, toStorableMark } from '../lib/marksLibrary';
import { sanitizeMarkId, findMarkIdConflict } from '../lib/markId';
import { formatDms } from '../lib/dms';

const SECTIONS = [
  {
    type: 'racing_mark',
    title: 'Racing Marks',
    hint: 'Fixed racing marks (A-Z, numbered marks). Positions used for course distance and bearing calculations.',
    showAppearance: true,
    showLights: true,
  },
  {
    type: 'shore_station',
    title: 'Shore Stations',
    hint: 'Fixed shore-based start locations (e.g. West Pier Hut, Corinthian YC). Each can have a VHF channel.',
    showVhf: true,
  },
  {
    type: 'committee_boat',
    title: 'Committee Boats',
    hint: 'Committee boats used for starts. Position can be updated manually or via AIS.',
    showVhf: true,
    showLive: true,
  },
];

/** Slug for a mark the officer gave no code for — derived from the NAME, so it
 *  stays a lower-case slug like every other generated id in the portal. An id
 *  the officer actually types goes through `sanitizeMarkId`, which preserves
 *  case (ra-15xp). */
function generateId(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');
}

/**
 * Parse coordinate from various formats:
 * - Decimal degrees: "53.285" or "-6.002"
 * - Nautical (deg min.dec): "N53 17.110" or "W6 00.100" or "53 17.110N"
 * - Degrees minutes: "53°17.110'" or "53 17.110"
 * Returns decimal degrees (negative for S/W), or null if invalid.
 */
function parseCoord(val) {
  if (val == null) return null;
  const s = String(val).trim();
  if (!s) return null;

  // Try plain decimal first
  if (/^-?\d+(\.\d+)?$/.test(s)) {
    const n = parseFloat(s);
    return isNaN(n) ? null : n;
  }

  // Nautical format: optional N/S/E/W prefix, degrees, space/°, minutes.decimal, optional N/S/E/W suffix
  const m = s.match(/^([NSEW])?\s*(\d+)[°\s]+(\d+(?:\.\d+)?)['\s]*([NSEW])?$/i);
  if (m) {
    const dir = (m[1] || m[4] || '').toUpperCase();
    const deg = parseInt(m[2], 10);
    const min = parseFloat(m[3]);
    if (isNaN(deg) || isNaN(min)) return null;
    let decimal = deg + min / 60;
    if (dir === 'S' || dir === 'W') decimal = -decimal;
    return decimal;
  }

  // Fallback to parseFloat for partial input
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

/**
 * Try to parse a pasted string as a lat/lng pair.
 * Supported formats:
 *   "53.303, -6.131"              (Google Maps decimal)
 *   "N53 17.110 W6 00.100"       (nautical pair)
 *   "53°17.110'N, 6°00.100'W"    (DMS-ish)
 * Returns { lat, lng } or null if not a pair.
 */
function parseCoordPair(val) {
  if (val == null) return null;
  const s = String(val).trim();
  if (!s) return null;

  // Decimal pair: "53.303, -6.131" or "53.303 -6.131"
  const decMatch = s.match(/^(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)$/);
  if (decMatch) {
    const lat = parseFloat(decMatch[1]);
    const lng = parseFloat(decMatch[2]);
    if (!isNaN(lat) && !isNaN(lng)) return { lat, lng };
  }

  // Nautical pair: "N53 17.110 W6 00.100" or "53 17.110N 6 00.100W"
  // Match two nautical coords separated by space
  const nauMatch = s.match(
    /^([NSEW])?\s*(\d+)[°\s]+(\d+(?:\.\d+)?)['\s]*([NSEW])?\s+([NSEW])?\s*(\d+)[°\s]+(\d+(?:\.\d+)?)['\s]*([NSEW])?$/i,
  );
  if (nauMatch) {
    const dir1 = (nauMatch[1] || nauMatch[4] || '').toUpperCase();
    const dir2 = (nauMatch[5] || nauMatch[8] || '').toUpperCase();
    const d1 = parseInt(nauMatch[2], 10) + parseFloat(nauMatch[3]) / 60;
    const d2 = parseInt(nauMatch[6], 10) + parseFloat(nauMatch[7]) / 60;
    const v1 = (dir1 === 'S' || dir1 === 'W') ? -d1 : d1;
    const v2 = (dir2 === 'S' || dir2 === 'W') ? -d2 : d2;
    // Figure out which is lat and which is lng from the direction letters
    if (dir1 === 'N' || dir1 === 'S') return { lat: v1, lng: v2 };
    if (dir1 === 'E' || dir1 === 'W') return { lat: v2, lng: v1 };
    // No direction letters — assume lat, lng order
    return { lat: v1, lng: v2 };
  }

  return null;
}

/** Format event_id like "2025-summer" into "Summer 2025". */
function formatEventLabel(eventId) {
  const parts = eventId.split('-');
  if (parts.length === 2) {
    const [year, season] = parts;
    return `${season.charAt(0).toUpperCase() + season.slice(1)} ${year}`;
  }
  return eventId;
}

export default function MarksManager() {
  const { clubId } = useParams();
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [marks, setMarks] = useState([]);
  const [marksSource, setMarksSource] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null);

  // Add-mark form state
  const [addingType, setAddingType] = useState(null);
  const [draft, setDraft] = useState({ name: '', code: '', coords: '', latitude: '', longitude: '', appearance: '', lights: '', vhf: '' });

  // Inline edit tracking
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  // Map ↔ list selection (two-way highlight)
  const [selectedMapId, setSelectedMapId] = useState(null);

  // Every mark mutation (drag, add, edit, delete, import) is a read-modify-write
  // of the whole WIP marks array, so two that overlap can both read the same
  // stale array and the later write clobbers the earlier one. Draggable markers
  // make overlap realistic (a fast background drag-save vs. a row save). Route
  // ALL writes through one promise chain so they apply strictly in order, each
  // seeing the previous one's result.
  const writeQueueRef = useRef(Promise.resolve());
  const enqueueWrite = useCallback((op) => {
    const run = writeQueueRef.current.then(op, op);
    // Keep the chain alive whatever this op's outcome.
    writeQueueRef.current = run.then(() => {}, () => {});
    return run;
  }, []);

  // Bulk import
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');

  // Library import (official Irish Lights AtoN feed + cross-club reuse)
  const [showLibrary, setShowLibrary] = useState(false);
  const [librarySource, setLibrarySource] = useState('official'); // 'official' | 'club'
  const [libFilter, setLibFilter] = useState(''); // name filter for the official feed
  const [libClubs, setLibClubs] = useState(null); // { clubs, seasonMap }
  const [libSourceClub, setLibSourceClub] = useState('');
  const [libSourceEvent, setLibSourceEvent] = useState('');
  const [libCandidates, setLibCandidates] = useState(null); // raw candidate marks
  const [libSelected, setLibSelected] = useState(() => new Set());
  const [libLoading, setLibLoading] = useState(false);
  // Monotonic token to drop stale async source loads (codex review): the live
  // Irish Lights fetch and cross-club season loads both race, so a slower earlier
  // request must not overwrite a newer selection.
  const libReqRef = useRef(0);

  const librarySummary = useMemo(
    () => (libCandidates ? summarizeLibraryImport(libCandidates, marks) : null),
    [libCandidates, marks],
  );

  // Load events on mount
  useEffect(() => {
    async function loadEvents() {
      try {
        const clubEvents = await getClubEvents(clubId);
        const sorted = [...clubEvents].sort((a, b) => ((b.date || b.id) > (a.date || a.id) ? 1 : -1));
        setEvents(sorted);
        if (sorted.length > 0) {
          setSelectedEventId(sorted[0].id);
        }
      } catch (err) {
        setError('Failed to load events: ' + err.message);
      }
    }
    loadEvents();
  }, [clubId]);

  const reload = useCallback(async () => {
    if (!selectedEventId) {
      setMarks([]);
      setMarksSource('');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { marks: data, source } = await marksStore.loadMarks(clubId, selectedEventId);
      data.sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
      setMarks(data);
      setMarksSource(source);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clubId, selectedEventId]);

  useEffect(() => { reload(); }, [reload]);

  const handleEventChange = (e) => {
    setSelectedEventId(e.target.value);
    setEditingId(null);
    setAddingType(null);
    setSelectedMapId(null);
  };

  // Drag-drop on the map: write the new position straight back to WIP and
  // reflect it locally at once, so the map updates LIVE with no draft/publish
  // reload toggle (ra-gbrd.15). The persist goes through the shared write queue
  // so it can't interleave with a row edit/import. On failure, reload from the
  // source of truth.
  const handleMapMove = useCallback((markId, lat, lng) => {
    setMarks((prev) => prev.map((m) => (m.id === markId ? { ...m, latitude: lat, longitude: lng } : m)));
    setSelectedMapId(markId);
    setSaving(markId);
    enqueueWrite(async () => {
      try {
        await marksStore.updateMark(clubId, selectedEventId, markId, { latitude: lat, longitude: lng });
      } catch (err) {
        setError('Error moving mark: ' + err.message);
        await reload();
      } finally {
        setSaving((cur) => (cur === markId ? null : cur));
      }
    });
  }, [clubId, selectedEventId, reload, enqueueWrite]);

  // Click empty water → open the racing-mark add form pre-filled with that point,
  // so an admin can quickly fill in a 'missing' mark without hunting for coords.
  const handleMapAddAt = useCallback((lat, lng) => {
    setError(null);
    setAddingType('racing_mark');
    setDraft({
      name: '', code: '',
      coords: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      latitude: String(lat), longitude: String(lng),
      appearance: '', lights: '', vhf: '',
    });
  }, []);

  const handleAdd = async (type) => {
    const name = draft.name.trim();
    if (!name) { setError('Name is required.'); return; }
    const lat = parseCoord(draft.latitude);
    const lng = parseCoord(draft.longitude);
    if (lat == null || lng == null) { setError('Valid latitude and longitude are required.'); return; }

    const id = draft.code.trim() ? sanitizeMarkId(draft.code) : generateId(name);
    const clash = findMarkIdConflict(marks, id);
    if (clash) { setError(`A mark with ID "${clash.id}" already exists.`); return; }

    setSaving(id);
    try {
      const mark = { id, name, latitude: lat, longitude: lng, type };
      if (draft.appearance.trim()) mark.appearance = draft.appearance.trim();
      if (draft.lights.trim()) mark.lights = draft.lights.trim();
      if (draft.vhf.trim()) mark.vhf = draft.vhf.trim();
      await enqueueWrite(() => marksStore.saveMark(clubId, selectedEventId, mark));
      setDraft({ name: '', code: '', coords: '', latitude: '', longitude: '', appearance: '', lights: '', vhf: '' });
      setAddingType(null);
      await reload();
    } catch (err) {
      setError('Error adding mark: ' + err.message);
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (markId) => {
    setSaving(markId);
    try {
      await enqueueWrite(() => marksStore.deleteMark(clubId, selectedEventId, markId));
      await reload();
    } catch (err) {
      setError('Error deleting mark: ' + err.message);
    } finally {
      setSaving(null);
    }
  };

  const handleBulkImport = async () => {
    if (!selectedEventId) return;
    const lines = importText.split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) { setError('No marks to import.'); return; }

    const parsed = [];
    for (const line of lines) {
      // Split on commas, pipes, or tabs — flexible CSV-ish format
      // Format: Name, code, lat, lng[, appearance[, lights]]
      // Also handle: Name │ code │ lat lng │ appearance │ lights
      const parts = line.split(/[,│|;\t]+/).map((p) => p.trim()).filter(Boolean);
      if (parts.length < 3) { setError(`Cannot parse line: "${line}"`); return; }

      let name, code, lat, lng, appearance, lights;

      if (parts.length === 3) {
        // Name │ code │ "lat lng" (coords together)
        name = parts[0];
        code = parts[1];
        const coordPair = parseCoordPair(parts[2]);
        if (!coordPair) { setError(`Cannot parse coords in: "${line}"`); return; }
        lat = coordPair.lat;
        lng = coordPair.lng;
      } else {
        // Name, code, lat, lng[, appearance[, lights]]
        name = parts[0];
        code = parts[1];
        lat = parseCoord(parts[2]);
        lng = parseCoord(parts[3]);
        appearance = parts[4] || '';
        lights = parts[5] || '';
      }

      if (lat == null || lng == null) { setError(`Invalid coords in: "${line}"`); return; }

      // Case-preserving (ra-15xp) — but an import is an UPDATE of a mark the
      // season already has, and `saveMark` matches exact-case. Re-importing `T`
      // into a season whose record is still keyed `t` would otherwise add a
      // case-twin, creating exactly the `T`/`t` ambiguity that nothing
      // downstream can resolve. So resolve the entered code to an existing
      // mark's spelling when the two differ only by case, and let a genuinely
      // new mark keep the case that was typed. `parsed` is included so one
      // paste containing both spellings can't split into two records either.
      const entered = sanitizeMarkId(code);
      const existing = findMarkIdConflict([...marks, ...parsed], entered);
      const id = existing ? existing.id : entered;
      parsed.push({ id, name, latitude: lat, longitude: lng, type: 'racing_mark',
        ...(appearance ? { appearance } : {}),
        ...(lights ? { lights } : {}),
      });
    }

    setSaving('import');
    try {
      // One queued op so the whole import is atomic relative to other writes.
      await enqueueWrite(async () => {
        for (const mark of parsed) {
          await marksStore.saveMark(clubId, selectedEventId, mark);
        }
      });
      setShowImport(false);
      setImportText('');
      await reload();
    } catch (err) {
      setError('Import error: ' + err.message);
    } finally {
      setSaving(null);
    }
  };

  // ── Library import ───────────────────────────────────────────────────────
  const resetLibrary = () => {
    libReqRef.current++; // cancel any in-flight source load
    setLibLoading(false); // its finally() will skip the reset, so clear it here
    setShowLibrary(false);
    setLibFilter('');
    setLibSourceClub('');
    setLibSourceEvent('');
    setLibCandidates(null);
    setLibSelected(new Set());
  };

  // Fetch the Irish Lights AtoN feed LIVE (never a bundled snapshot). Uses the
  // monotonic token so a slower earlier fetch can't overwrite a newer selection.
  // Selection defaults to none: the live feed carries every official light in
  // the country, so the officer picks the handful their course needs.
  const loadOfficialMarks = async () => {
    const token = ++libReqRef.current;
    setLibLoading(true);
    setError(null);
    try {
      const official = await fetchIrishLightsMarks();
      if (token !== libReqRef.current) return; // superseded
      setCandidates(official, false);
    } catch (err) {
      if (token !== libReqRef.current) return;
      setError('Failed to load the live Irish Lights feed: ' + err.message);
      setCandidates(null);
    } finally {
      if (token === libReqRef.current) setLibLoading(false);
    }
  };

  const openLibrary = async () => {
    libReqRef.current++; // ignore any load still in flight from a prior open
    setLibLoading(false);
    setShowLibrary(true);
    setLibrarySource('official');
    setLibFilter('');
    setLibSourceClub('');
    setLibSourceEvent('');
    setLibCandidates(null);
    setLibSelected(new Set());
    // Fetch the live official feed for the default tab.
    loadOfficialMarks();
    // Pre-load the club list for the cross-club source (best-effort).
    if (!libClubs) {
      try {
        const data = await loadAllClubs();
        setLibClubs(data);
      } catch (err) {
        setError('Failed to load clubs: ' + err.message);
      }
    }
  };

  // When candidate marks change, default the selection to everything importable
  // (unless autoSelect is false — used by the large live official feed).
  const setCandidates = (candidates, autoSelect = true) => {
    setLibCandidates(candidates);
    if (candidates && autoSelect) {
      const summary = summarizeLibraryImport(candidates, marks);
      setLibSelected(new Set(importableIds(summary)));
    } else {
      setLibSelected(new Set());
    }
  };

  // Switch the library source tab. Bumps the request token so any in-flight
  // load can't apply after the tab changed (codex review).
  const switchLibrarySource = (src) => {
    libReqRef.current++;
    setLibLoading(false);
    setLibrarySource(src);
    if (src === 'official') {
      setLibSourceClub('');
      setLibSourceEvent('');
      loadOfficialMarks();
    } else {
      setLibFilter('');
      setCandidates(null);
    }
  };

  const handleSourceClubChange = (cId) => {
    libReqRef.current++; // cancel any in-flight load for the previous club
    setLibLoading(false); // stale request's finally() won't reset it
    setLibSourceClub(cId);
    setLibSourceEvent('');
    setCandidates(null);
  };

  const handleSourceEventChange = async (eId) => {
    setLibSourceEvent(eId);
    if (!eId || !libSourceClub) { setCandidates(null); return; }
    const reqClub = libSourceClub;
    const token = ++libReqRef.current;
    setLibLoading(true);
    setError(null);
    try {
      // Cross-club reuse reads the source season's PUBLISHED bundle only.
      // Firestore allows public reads of `bundle/*` but restricts `wip/*` to
      // writers of that same club, so reading another club's WIP would hit
      // permission-denied for a ClubAdmin (codex review, ra-gbrd.16). Published
      // bundles are also the right canonical source for reuse — unpublished
      // drafts aren't shared marks yet.
      const published = await getEventPublishedBundles(reqClub, eId);
      // Drop the result if a newer selection superseded this request (codex review).
      if (token !== libReqRef.current) return;
      const srcMarks = published.length > 0 ? (published[0].marks || []) : [];
      setCandidates(srcMarks);
    } catch (err) {
      if (token !== libReqRef.current) return;
      setError('Failed to load source marks: ' + err.message);
      setCandidates(null);
    } finally {
      if (token === libReqRef.current) setLibLoading(false);
    }
  };

  const toggleLibMark = (id) => {
    setLibSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleLibraryImport = async () => {
    if (!librarySummary) return;
    const toImport = pickImportableMarks(librarySummary, libSelected)
      // Official-buoy marks carry library-only metadata — strip it before saving.
      .map((m) => toStorableMark(m));
    if (toImport.length === 0) return;
    setSaving('library');
    setError(null);
    try {
      // One queued op so the whole import is atomic relative to other writes
      // (drag/edit/delete) — they share a read-modify-write of the WIP marks
      // array, so an overlap would clobber. Matches handleBulkImport.
      await enqueueWrite(async () => {
        for (const mark of toImport) {
          await marksStore.saveMark(clubId, selectedEventId, mark);
        }
      });
      resetLibrary();
      await reload();
    } catch (err) {
      setError('Import error: ' + err.message);
    } finally {
      setSaving(null);
    }
  };

  const startEdit = (mark) => {
    setEditingId(mark.id);
    setEditDraft({
      name: mark.name || '',
      code: mark.id || '',
      coords: (mark.latitude != null && mark.longitude != null) ? `${mark.latitude}, ${mark.longitude}` : '',
      latitude: String(mark.latitude ?? ''),
      longitude: String(mark.longitude ?? ''),
      appearance: mark.appearance || '',
      lights: mark.lights || '',
      vhf: mark.vhf || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({});
  };

  const handleSaveEdit = async (markId) => {
    const lat = parseCoord(editDraft.latitude);
    const lng = parseCoord(editDraft.longitude);
    if (lat == null || lng == null) { setError('Valid latitude and longitude are required.'); return; }
    if (!editDraft.name.trim()) { setError('Name is required.'); return; }

    // Case-preserving (ra-15xp). This ran on EVERY save, not just when the
    // officer touched the code field, so simply moving mark `T` used to
    // delete-and-recreate it as `t` — silently breaking every exact-case lookup.
    const newCode = sanitizeMarkId(editDraft.code);
    const idChanged = newCode && newCode !== markId;
    if (idChanged) {
      const clash = findMarkIdConflict(marks, newCode, markId);
      if (clash) {
        setError(`A mark with code "${clash.id}" already exists.`);
        return;
      }
    }

    setSaving(markId);
    try {
      // Queue as one atomic op so the (delete + re-create) on an id change can't
      // interleave with another mark write.
      await enqueueWrite(async () => {
        if (idChanged) {
          // ID changed — delete old and create new
          const oldMark = marks.find((m) => m.id === markId);
          const newMark = {
            id: newCode,
            name: editDraft.name.trim(),
            latitude: lat,
            longitude: lng,
            type: oldMark?.type || 'racing_mark',
          };
          if (editDraft.appearance.trim()) newMark.appearance = editDraft.appearance.trim();
          if (editDraft.lights.trim()) newMark.lights = editDraft.lights.trim();
          if (editDraft.vhf.trim()) newMark.vhf = editDraft.vhf.trim();
          await marksStore.deleteMark(clubId, selectedEventId, markId);
          await marksStore.saveMark(clubId, selectedEventId, newMark);
        } else {
          const fields = {
            name: editDraft.name.trim(),
            latitude: lat,
            longitude: lng,
          };
          if (editDraft.appearance.trim()) fields.appearance = editDraft.appearance.trim();
          if (editDraft.lights.trim()) fields.lights = editDraft.lights.trim();
          if (editDraft.vhf.trim()) fields.vhf = editDraft.vhf.trim();
          await marksStore.updateMark(clubId, selectedEventId, markId, fields);
        }
      });
      setEditingId(null);
      setEditDraft({});
      await reload();
    } catch (err) {
      setError('Error updating mark: ' + err.message);
    } finally {
      setSaving(null);
    }
  };

  if (loading && events.length === 0) return <div className="page"><p>Loading...</p></div>;

  return (
    <div className="page">
      <header className="portal-header">
        <Link to={`/clubs/${clubId}`} className="back-link">&larr; Club</Link>
        <h1>Marks &amp; Locations</h1>
      </header>

      <PublishStateNote mode="draft">
        <p>
          Mark edits (add, move, import, delete) are saved to this season&rsquo;s
          draft. Publish them from{' '}
          <Link to={`/clubs/${clubId}/course-cards`}>Course Cards → Create Bundle</Link>
          {' '}(the review dialog lists your mark changes), then{' '}
          <Link to={`/clubs/${clubId}/courses/history`}>Version History → Activate</Link>
          {' '}to make them live.
        </p>
      </PublishStateNote>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
        <label htmlFor="event-select" style={{ fontWeight: 600 }}>Season:</label>
        <select
          id="event-select"
          value={selectedEventId}
          onChange={handleEventChange}
          style={{ padding: '0.25rem 0.5rem' }}
        >
          {events.length === 0 && <option value="">No events found</option>}
          {events.map((ev) => (
            <option key={ev.id} value={ev.id}>{formatEventLabel(ev.id)}</option>
          ))}
        </select>
        <span style={{ fontSize: '0.85rem', color: '#888' }}>
          {marks.length} mark{marks.length !== 1 ? 's' : ''}
          {marksSource && marksSource !== 'none' ? ` (from ${marksSource})` : ''}
        </span>
        {selectedEventId && (
          <button className="btn btn-sm" onClick={() => setShowImport(!showImport)}>
            {showImport ? 'Cancel Import' : 'Paste Import'}
          </button>
        )}
        {selectedEventId && (
          <button className="btn btn-sm" onClick={openLibrary}>
            Import from library
          </button>
        )}
      </div>

      {showImport && (
        <div className="config-section" style={{ marginBottom: '1rem' }}>
          <h3>Paste Marks</h3>
          <p style={{ fontSize: '0.85rem', color: '#888', margin: '0.25rem 0 0.5rem' }}>
            One mark per line: Name, Code, Lat, Lng, Appearance, Lights
          </p>
          <textarea
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            rows={8}
            style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.85rem', padding: '0.5rem' }}
            placeholder="East Kish Buoy, ekb, 53.239050, -5.893250, Red Can, Fl(2) R 10s"
          />
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button className="btn btn-primary" onClick={handleBulkImport} disabled={saving === 'import' || !importText.trim()}>
              {saving === 'import' ? 'Importing...' : `Import ${importText.trim().split('\n').filter(Boolean).length} marks`}
            </button>
            <button className="btn btn-sm" onClick={() => { setShowImport(false); setImportText(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {error && (
        <div className="error-banner">
          {error}
          <button className="btn btn-sm" style={{ marginLeft: '0.5rem' }} onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {loading ? (
        <p>Loading marks...</p>
      ) : events.length === 0 ? (
        <div className="empty-state">
          <p>No seasons yet — marks live inside a season, so there&rsquo;s nothing to add to until you create one.</p>
          <Link to={`/clubs/${clubId}/seasons`} className="btn btn-primary">
            Create a season first &rarr;
          </Link>
        </div>
      ) : !selectedEventId ? (
        <p className="empty-state">Select a season to manage marks.</p>
      ) : (
      <>
        <section className="config-section">
          <h2>Map</h2>
          <p className="section-hint">
            Chart mark editor. Drag a marker for a coarse move, or select one and use the
            arrow keys to nudge it in fine degree-minute steps (Shift = ×10) — both save to the
            draft immediately (publish to make them live — see the note above).
            The selected mark's position shows top-left in degrees and decimal minutes. Toggle
            the legend to show/hide marks by type. Click empty water to add a mark there.
          </p>
          <div className="marks-map-panel">
            {/* Remount on season change so the map re-centres + refits to the new
                season's marks (a stale fit-guard / MapContainer can't re-apply
                center after mount — codex ra-gbrd.15). Drag stays snap-free since
                a same-season reposition doesn't change the key. */}
            <MarksMap
              key={selectedEventId}
              marks={marks}
              selectedId={selectedMapId}
              onSelect={setSelectedMapId}
              onMoveMark={handleMapMove}
              onAddAt={handleMapAddAt}
            />
          </div>
        </section>
        {SECTIONS.map((section) => {
          const sectionMarks = marks.filter((m) => (m.type || 'racing_mark') === section.type);

          return (
            <section key={section.type} className="config-section">
              <h2>{section.title}</h2>
              <p className="section-hint">{section.hint}</p>

              {sectionMarks.length === 0 && addingType !== section.type && (
                <p className="empty-state">No {section.title.toLowerCase()} defined yet.</p>
              )}

              {sectionMarks.length > 0 && (
                <table className="config-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Code</th>
                      <th>Latitude</th>
                      <th>Longitude</th>
                      {section.showAppearance && <th>Appearance</th>}
                      {section.showLights && <th>Lights</th>}
                      {section.showVhf && <th>VHF</th>}
                      {section.showLive && <th>Live</th>}
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sectionMarks.map((mark) => {
                      const isEditing = editingId === mark.id;
                      const isSelected = selectedMapId === mark.id;
                      return (
                        <tr
                          key={mark.id}
                          onClick={() => setSelectedMapId(mark.id)}
                          style={isSelected ? { background: 'rgba(37,99,235,0.12)', cursor: 'pointer' } : { cursor: 'pointer' }}
                        >
                          <td>
                            {isEditing ? (
                              <input
                                type="text"
                                className="inline-input"
                                value={editDraft.name}
                                onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                              />
                            ) : mark.name}
                          </td>
                          <td>
                            {isEditing ? (
                              <input
                                type="text"
                                className="inline-input"
                                style={{ width: '80px' }}
                                value={editDraft.code}
                                onChange={(e) => setEditDraft({ ...editDraft, code: e.target.value })}
                                title="Short code used in course sequences"
                              />
                            ) : <code>{mark.id}</code>}
                          </td>
                          {isEditing ? (
                            <td colSpan={2}>
                              <input
                                type="text"
                                className="inline-input"
                                style={{ width: '220px' }}
                                value={editDraft.coords}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const pair = parseCoordPair(val);
                                  if (pair) {
                                    setEditDraft({ ...editDraft, coords: val, latitude: String(pair.lat), longitude: String(pair.lng) });
                                  } else {
                                    setEditDraft({ ...editDraft, coords: val });
                                  }
                                }}
                                placeholder="53.303, -6.131 or N53 17.110 W6 00.100"
                              />
                              {editDraft.latitude && editDraft.longitude && parseCoord(editDraft.latitude) != null && parseCoord(editDraft.longitude) != null && (
                                <span style={{ fontSize: '0.75rem', color: '#888', marginLeft: '0.35rem' }}>
                                  {parseCoord(editDraft.latitude).toFixed(6)}, {parseCoord(editDraft.longitude).toFixed(6)}
                                </span>
                              )}
                            </td>
                          ) : (
                            <>
                              <td><code title={Number.isFinite(mark.latitude) ? `${mark.latitude.toFixed(6)}° (decimal)` : undefined} style={{ cursor: 'help' }}>{formatDms(mark.latitude, 'lat')}</code></td>
                              <td><code title={Number.isFinite(mark.longitude) ? `${mark.longitude.toFixed(6)}° (decimal)` : undefined} style={{ cursor: 'help' }}>{formatDms(mark.longitude, 'lng')}</code></td>
                            </>
                          )}
                          {section.showAppearance && (
                            <td>
                              {isEditing ? (
                                <input
                                  type="text"
                                  className="inline-input"
                                  value={editDraft.appearance}
                                  onChange={(e) => setEditDraft({ ...editDraft, appearance: e.target.value })}
                                />
                              ) : (mark.appearance || '—')}
                            </td>
                          )}
                          {section.showLights && (
                            <td>
                              {isEditing ? (
                                <input
                                  type="text"
                                  className="inline-input"
                                  value={editDraft.lights}
                                  onChange={(e) => setEditDraft({ ...editDraft, lights: e.target.value })}
                                  placeholder="e.g. Fl(2) R 10s"
                                />
                              ) : (mark.lights || '—')}
                            </td>
                          )}
                          {section.showVhf && (
                            <td>
                              {isEditing ? (
                                <input
                                  type="text"
                                  className="inline-input"
                                  style={{ width: '60px' }}
                                  value={editDraft.vhf}
                                  onChange={(e) => setEditDraft({ ...editDraft, vhf: e.target.value })}
                                />
                              ) : (mark.vhf || '—')}
                            </td>
                          )}
                          {section.showLive && (
                            <td>{mark.isLive ? 'Yes' : 'No'}</td>
                          )}
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {isEditing ? (
                              <>
                                <button
                                  className="btn btn-primary btn-sm"
                                  disabled={saving === mark.id}
                                  onClick={() => handleSaveEdit(mark.id)}
                                >
                                  Save
                                </button>{' '}
                                <button className="btn btn-sm" onClick={cancelEdit}>Cancel</button>
                              </>
                            ) : (
                              <>
                                <button className="btn btn-sm" onClick={() => startEdit(mark)}>Edit</button>{' '}
                                <button
                                  className="btn btn-danger btn-sm"
                                  disabled={saving === mark.id}
                                  onClick={() => handleDelete(mark.id)}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {addingType === section.type ? (
                <div className="add-form" style={{ marginTop: '0.5rem' }}>
                  <div className="form-row">
                    <input
                      type="text"
                      placeholder="Name (e.g. Battery T)"
                      value={draft.name}
                      onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                      className="inline-input"
                      autoFocus
                      onKeyDown={(e) => e.key === 'Enter' && handleAdd(section.type)}
                    />
                    <input
                      type="text"
                      placeholder="Code (e.g. MUG)"
                      value={draft.code}
                      onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                      className="inline-input"
                      style={{ width: '80px' }}
                      title="Short code for course sequences. Leave blank to auto-generate from name."
                    />
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                      <input
                        type="text"
                        placeholder="53.303, -6.131 or N53 17.110 W6 00.100"
                        value={draft.coords}
                        onChange={(e) => {
                          const val = e.target.value;
                          const pair = parseCoordPair(val);
                          if (pair) {
                            setDraft({ ...draft, coords: val, latitude: String(pair.lat), longitude: String(pair.lng) });
                          } else {
                            // Single value — try as lat, clear lng
                            setDraft({ ...draft, coords: val, latitude: val, longitude: '' });
                          }
                        }}
                        className="inline-input"
                        style={{ width: '260px' }}
                      />
                      {draft.latitude && draft.longitude && parseCoord(draft.latitude) != null && parseCoord(draft.longitude) != null && (
                        <span style={{ fontSize: '0.75rem', color: '#888', whiteSpace: 'nowrap' }}>
                          {parseCoord(draft.latitude).toFixed(6)}, {parseCoord(draft.longitude).toFixed(6)}
                        </span>
                      )}
                    </span>
                    {section.showAppearance && (
                      <input
                        type="text"
                        placeholder="Appearance"
                        value={draft.appearance}
                        onChange={(e) => setDraft({ ...draft, appearance: e.target.value })}
                        className="inline-input"
                        style={{ width: '120px' }}
                      />
                    )}
                    {section.showLights && (
                      <input
                        type="text"
                        placeholder="Lights (e.g. Fl(2) R 10s)"
                        value={draft.lights}
                        onChange={(e) => setDraft({ ...draft, lights: e.target.value })}
                        className="inline-input"
                        style={{ width: '140px' }}
                      />
                    )}
                    {section.showVhf && (
                      <input
                        type="text"
                        placeholder="VHF (e.g. 68)"
                        value={draft.vhf}
                        onChange={(e) => setDraft({ ...draft, vhf: e.target.value })}
                        className="inline-input"
                        style={{ width: '80px' }}
                      />
                    )}
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={saving != null}
                      onClick={() => handleAdd(section.type)}
                    >
                      Add
                    </button>
                    <button className="btn btn-sm" onClick={() => setAddingType(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button
                  className="btn btn-sm"
                  style={{ marginTop: '0.5rem' }}
                  onClick={() => {
                    setAddingType(section.type);
                    setDraft({ name: '', code: '', coords: '', latitude: '', longitude: '', appearance: '', lights: '', vhf: '' });
                  }}
                >
                  + Add {section.title.replace(/s$/, '')}
                </button>
              )}
            </section>
          );
        })}
      </>
      )}

      {showLibrary && (
        <div className="confirm-overlay">
          <div className="confirm-dialog card-import-dialog">
            <h3>Import marks from library</h3>
            <p style={{ fontSize: '0.85rem', color: '#888', margin: '0 0 0.75rem' }}>
              Pick official aids or reuse marks already entered for another club &mdash;
              no hand-keying GPS.
            </p>

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <button
                className={`btn btn-sm${librarySource === 'official' ? ' btn-primary' : ''}`}
                onClick={() => switchLibrarySource('official')}
              >
                Official buoys
              </button>
              <button
                className={`btn btn-sm${librarySource === 'club' ? ' btn-primary' : ''}`}
                onClick={() => switchLibrarySource('club')}
              >
                Another club / season
              </button>
            </div>

            {librarySource === 'official' ? (
              <>
                <p style={{ fontSize: '0.75rem', color: '#888', margin: '0 0 0.5rem' }}>
                  Live from the{' '}
                  <a
                    href="https://data.gov.ie/dataset/irishlightsaton"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Irish Lights Aids-to-Navigation
                  </a>{' '}
                  feed (CC BY 4.0), fetched now &mdash; not a stored snapshot.{' '}
                  <button
                    className="btn btn-sm"
                    style={{ marginLeft: '0.25rem' }}
                    onClick={loadOfficialMarks}
                    disabled={libLoading}
                  >
                    Refresh
                  </button>
                </p>
                <label className="form-field">
                  <span className="form-label">Filter:</span>
                  <input
                    type="text"
                    value={libFilter}
                    onChange={(e) => setLibFilter(e.target.value)}
                    placeholder="e.g. Roches, Cork, Fastnet"
                  />
                </label>
              </>
            ) : (
              <>
                <label className="form-field">
                  <span className="form-label">Club:</span>
                  <select
                    value={libSourceClub}
                    onChange={(e) => handleSourceClubChange(e.target.value)}
                    disabled={!libClubs}
                  >
                    <option value="" disabled>{libClubs ? 'Select club...' : 'Loading clubs...'}</option>
                    {libClubs?.clubs.map((c) => (
                      <option key={c.id} value={c.id}>{c.name || c.id}</option>
                    ))}
                  </select>
                </label>
                {libSourceClub && (
                  <label className="form-field">
                    <span className="form-label">Season:</span>
                    <select value={libSourceEvent} onChange={(e) => handleSourceEventChange(e.target.value)}>
                      <option value="" disabled>Select season...</option>
                      {(libClubs?.seasonMap[libSourceClub] || []).map((ev) => (
                        <option key={ev.id} value={ev.id}>{formatEventLabel(ev.id)}</option>
                      ))}
                    </select>
                  </label>
                )}
              </>
            )}

            {libLoading && <p>Loading marks...</p>}

            {librarySummary && !libLoading && (
              librarySummary.total === 0 ? (
                <p>No marks found.</p>
              ) : (
                <div className="mark-import-summary" style={{ marginTop: '0.5rem' }}>
                  <p style={{ fontSize: '0.85rem' }}>
                    {librarySummary.newCount} new
                    {librarySummary.existingCount > 0 && `, ${librarySummary.existingCount} already present`}
                    {librarySummary.pendingCount > 0 && `, ${librarySummary.pendingCount} awaiting position`}
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0.25rem 0', maxHeight: '260px', overflowY: 'auto' }}>
                    {librarySummary.items
                      .filter(({ mark }) => {
                        const q = libFilter.trim().toLowerCase();
                        if (!q) return true;
                        return (
                          String(mark.name || '').toLowerCase().includes(q) ||
                          String(mark.id || '').toLowerCase().includes(q)
                        );
                      })
                      .map(({ mark, status }) => (
                      <li key={mark.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.15rem 0', opacity: status === 'new' ? 1 : 0.6 }}>
                        <input
                          type="checkbox"
                          disabled={status !== 'new'}
                          checked={status === 'new' && libSelected.has(String(mark.id))}
                          onChange={() => toggleLibMark(String(mark.id))}
                        />
                        <span style={{ fontWeight: 600 }}>{mark.name}</span>
                        <code style={{ fontSize: '0.75rem' }}>{mark.id}</code>
                        {mark.lights && <span style={{ fontSize: '0.75rem', color: '#888' }}>{mark.lights}</span>}
                        {status === 'existing' && <span style={{ fontSize: '0.7rem', color: '#888' }}>already present</span>}
                        {status === 'pending' && <span style={{ fontSize: '0.7rem', color: '#b8860b' }}>position to confirm</span>}
                        {status === 'new' && (
                          <span style={{ fontSize: '0.7rem', color: '#888' }}>
                            {mark.latitude.toFixed(4)}, {mark.longitude.toFixed(4)}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            )}

            <div className="confirm-actions">
              <button
                className="btn btn-primary"
                disabled={saving === 'library' || pickImportableMarks(librarySummary || { items: [] }, libSelected).length === 0}
                onClick={handleLibraryImport}
              >
                {saving === 'library'
                  ? 'Importing...'
                  : `Add ${pickImportableMarks(librarySummary || { items: [] }, libSelected).length} mark${pickImportableMarks(librarySummary || { items: [] }, libSelected).length !== 1 ? 's' : ''}`}
              </button>
              <button className="btn" onClick={resetLibrary}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
